import { createContext, ReactElement, ReactNode, useContext } from 'react';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';

import {
  schema,
  Schema,
} from '../components/HirePlanForm/data/schemaDependent';

import { useHirePlanFormStepStore } from '../stores/useHirePlanFormStepStore';
import { useBoostedPlanDataStore } from '../stores/useBoostedPlanDataStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import { useError } from '../stores/useError';
import { useResponseStore } from '../stores/useResponseStore';
import axios from 'axios';
import { useWebSocketAssinatura } from '../hooks/useWebSocketAssinatura';
import { useLinkStore } from '../stores/useLinkStore';
import { v4 as uuid } from 'uuid';

type PaymentFormSchema = Schema;

interface HirePlanFormContextProps {
  register: UseFormRegister<PaymentFormSchema>;
  control: Control<PaymentFormSchema, any>;
  watch: UseFormWatch<PaymentFormSchema>;
  handleSubmit: UseFormHandleSubmit<PaymentFormSchema>;
  onSubmit: (schema: PaymentFormSchema) => void;
  goToNextForm: (schema: PaymentFormSchema) => void;
  errors: FieldErrors<PaymentFormSchema>;
  clearErrors: UseFormClearErrors<PaymentFormSchema>;
  unregister: UseFormUnregister<PaymentFormSchema>;
  setValue: UseFormSetValue<PaymentFormSchema>;
  getValues: UseFormGetValues<PaymentFormSchema>;
  trigger: UseFormTrigger<PaymentFormSchema>;
  navigateToProposta: () => void;
}

const HirePlanFormContext = createContext({} as HirePlanFormContextProps);

interface HirePlanFormProviderProps {
  children: ReactNode;
}

export function UpdateDependentProvider({
  children,
}: HirePlanFormProviderProps): ReactElement {
  const initialPlanData = useBoostedPlanDataStore(state => state.data);
  const goToNextStep = useHirePlanFormStepStore(state => state.goToNextStep);
  const navigate = useNavigate();
  const storedResponse = useResponseStore(x => x.response);
  const { aguardarAssinatura } = useWebSocketAssinatura();

  const setLoading = useLoadingStore(state => state.setLoading);
  const setError = useError(state => state.setError);
  const setTitle = useError(state => state.setErrorTitle);
  const setText = useError(state => state.setErrorText);
  const setResponse = useResponseStore(x => x.setResponse);
  const setLinkData = useLinkStore(x => x.setLinkData);

  interface CheckoutData {
    token: string;
    cpf?: string;
    verifica: boolean;
    nomeTitular?: string;
    dataNascimento?: string;
    emailTitular?: string;
    cep?: string;
    idUf?: string;
    endereco?: string;
    numeroCasa?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    celular?: string;
    nomeMae?: string;
    matricula?: string;
    idSexo?: string;
    idEstadoCivil?: string;
    formaPagamento?: object;
    proposta: string;
    valor_Mensalidade: number;
    vencimentoBoleto?: string;
    vencimentoDebito?: string;
    vencimentoConsignado?: string;
    qtdVidas: string;
    dependentes?: object;
    dependenteSamePlan: boolean;
    idBanco?: string;
    conta?: string;
    agencia?: string;
    operacao?: string;
    primeiraBoleto?: boolean;
    responsavelFinanceiro: object;
    idOrgaoExpedidor: number;
    idOrgaoExpedidorUf: number;
    idFontePagadora: number;
    orgao: number;
  }

  const calculateDueDate = (): string => {
    const today = new Date();
    today.setMonth(today.getMonth() + 1);

    today.setDate(1);

    return today.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };
  const getMonthAndYear = (): string => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() + 2);
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const year = currentDate.getFullYear();

    return `${year}${month}`;
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    unregister,
    clearErrors,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<PaymentFormSchema>({
    resolver: zodResolver(schema),
  });

  const goToNextForm: SubmitHandler<PaymentFormSchema> = () => {
    goToNextStep();
  };
  const formatDate = (date: string) => {
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`;
  };
  const vigencia = new Date();
  vigencia.setDate(vigencia.getDate() + 30);

  const gerarDadosCheckout = async (
    data: PaymentFormSchema,
    MonthYear: number,
    flowId: string,
  ) => {
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const propostaNr = String(Math.floor(Math.random() * 1000000000));
    const dependents = data.dependents ?? [];
    let dependentesComTitular = [];
    if (data.Associado.selecionado.codigoDaEmpresa == 26710) {
      const hoje = new Date();
      const mesesParaVigencia = hoje.getDate() > 15 ? 2 : 1;
      vigencia.setFullYear(
        hoje.getFullYear(),
        hoje.getMonth() + mesesParaVigencia,
        1,
      );

      if (dependents.length > 0) {
        let titular = null;

        const conjuge = dependents.find(
          x => x.parentage === '10' || x.parentage === '3',
        );

        if (conjuge) {
          titular = conjuge;
        } else {
          const filhos = dependents.filter(
            x => x.parentage === '11' || x.parentage === '4',
          );

          if (filhos.length > 0) {
            titular = filhos.reduce((maisVelho, atual) => {
              return new Date(atual.birthDate) < new Date(maisVelho.birthDate)
                ? atual
                : maisVelho;
            });
          } else {
            titular = dependents[0];
          }
        }

        const dependentesSemTitular = titular
          ? dependents.filter(x => x.cpf !== titular.cpf)
          : dependents;

        dependentesComTitular = [
          {
            tipo: 1,
            nome: titular.name,
            cpf: titular.cpf?.replace(/[.-]/g, ''),
            sexo: titular.gender == '1' ? 0 : 1,
            plano: data.Associado.plano,
            planoValor: data?.Associado?.plano_vl?.replace(',', '.'),
            numeroProposta: propostaNr,
            carenciaAtendimento: 0,
            rcaId: 0,
            cns: data.Associado.selecionado?.cns || '',
            cd_orientacao_sexual: 0,
            OutraOrientacaoSexual: '',
            cd_ident_genero: 0,
            OutraIdentidadeGenero: '',
            idExterno: '',
            nomeMae: titular.mothersName,
            MMYYYY1Pagamento: MonthYear,
            numeroCarteira: '',
            observacaoUsuario: '',
            dataNascimento: formatDate(titular.birthDate),
            funcionarioCadastro: 62181,
            dataCadastroLoteContrato: '',
          },
          ...(dependentesSemTitular?.map(dependent => ({
            tipo:
              dependent.parentage == '10' || dependent.parentage == '3'
                ? 3
                : dependent.parentage == '11' || dependent.parentage == '4'
                ? 4
                : 10,
            nome: dependent.name,
            cpf: dependent.cpf?.replace(/[.-]/g, ''),
            sexo: dependent.gender == '1' ? 0 : 1,
            plano: dependent.plano
              ? parseFloat(dependent.plano)
              : data.Associado.plano,
            planoValor: dependent.plano_vl
              ? dependent.plano_vl.replace(',', '.')
              : data?.Associado?.plano_vl?.replace(',', '.'),
            cns: dependent.cns || '',
            numeroProposta: propostaNr,
            carenciaAtendimento: 0,
            rcaId: 0,
            cd_orientacao_sexual: 0,
            OutraOrientacaoSexual: '',
            cd_ident_genero: 0,
            OutraIdentidadeGenero: '',
            idExterno: '',
            estadoCivil: dependent.civilStatus,
            nomeMae: dependent.mothersName,
            MMYYYY1Pagamento: MonthYear,
            numeroCarteira: '',
            observacaoUsuario: '',
            dataNascimento: formatDate(dependent.birthDate),
            funcionarioCadastro: 62181,
            dataCadastroLoteContrato: '',
          })) || []),
        ];
      }
    }
    dependentesComTitular = [
      {
        tipo: 1,
        nome: data.Associado.name,
        cpf: data.Associado.cpf?.replace(/[.-]/g, ''),
        sexo: data.Associado.selecionado?.sexo == '1' ? 0 : 1,
        plano: data.Associado.plano,
        planoValor: data?.Associado?.plano_vl?.replace(',', '.'),
        numeroProposta: propostaNr,
        carenciaAtendimento: 0,
        rcaId: 0,
        cns: data.Associado.selecionado?.cns || '',
        cd_orientacao_sexual: 0,
        OutraOrientacaoSexual: '',
        cd_ident_genero: 0,
        OutraIdentidadeGenero: '',
        idExterno: '',
        nomeMae: data.Associado.name,
        MMYYYY1Pagamento: MonthYear,
        numeroCarteira: '',
        observacaoUsuario: '',
        dataNascimento: formatDate(data.Associado.birthDate),
        funcionarioCadastro: 62181,
        dataCadastroLoteContrato: '',
      },
      ...(dependents.map(dependent => ({
        tipo:
          dependent.parentage == '10' || dependent.parentage == '3'
            ? 3
            : dependent.parentage == '11' || dependent.parentage == '4'
            ? 4
            : 10,
        nome: dependent.name,
        cpf: dependent.cpf?.replace(/[.-]/g, ''),
        sexo: dependent.gender == '1' ? 0 : 1,
        plano: dependent.plano
          ? parseFloat(dependent.plano)
          : data.Associado.plano,
        planoValor: dependent.plano_vl
          ? dependent.plano_vl.replace(',', '.')
          : data?.Associado?.plano_vl?.replace(',', '.'),
        cns: dependent.cns || '',
        numeroProposta: propostaNr,
        carenciaAtendimento: 0,
        rcaId: 0,
        estadoCivil: dependent.civilStatus,
        cd_orientacao_sexual: 0,
        OutraOrientacaoSexual: '',
        cd_ident_genero: 0,
        OutraIdentidadeGenero: '',
        idExterno: '',
        nomeMae: dependent.mothersName,
        MMYYYY1Pagamento: MonthYear,
        numeroCarteira: '',
        observacaoUsuario: '',
        dataNascimento: formatDate(dependent.birthDate),
        funcionarioCadastro: 62181, // funcionario Ti
        dataCadastroLoteContrato: '',
      })) || []),
    ];
    const orgaoExp = initialPlanData.listaOrgaosExpedidor.find(
      x => x.id_oe === data.Associado.selecionado?.orgao,
    );
    const calculaValorAtual = data.Associado.selecionado.dependentes.reduce(
      (total, dependent) =>
        total +
        parseFloat(
          dependent.valorPlano?.replace('R$', '').trim().replace(',', '.'),
        ),
      0,
    );
    console.log(data.Associado.plano_vl.replace(',', '.'));
    const calculaValor =
      dependents.length * parseFloat(data.Associado.plano_vl.replace(',', '.'));
    const firstDependent = dependents[0];
    const changePlan =
      Boolean(firstDependent) &&
      data.Associado.plano !==
        data.Associado.selecionado.dependentes[0].codigoPlano;
    const endereco = await axios.post(
      `${process.env.REACT_APP_URL_BASE}/api/redeatendimento/Endereco?token=${process.env.REACT_APP_TOKEN_S4E_V2}&cep=${data.Associado.selecionado.cep}`,
    );
    const contatos = [];
    if (data.Associado.selecionado.contatos) {
      const celular = data.Associado.selecionado.contatos.find(
        x => x.tipo === 'Celular',
      );
      const email = data.Associado.selecionado.contatos.find(
        x => x.tipo === 'E-mail',
      );
      if (celular) {
        const newContato = {
          tipo: 8,
          dado: celular.descricaoContato,
        };
        contatos.push(newContato);
      }
      if (email) {
        const newContato = {
          tipo: 50,
          dado: email.descricaoContato,
        };
        contatos.push(newContato);
      }
    }

    const checkoutData = {
      token: token,
      WsId: flowId,
      dadosNovo: {
        changePlan: changePlan,
        hasNewDependente: data.dependents.length > 0,
        parcelaRetidaComissao: '0',
        incluirMensalidades: '1',
        parceiro: {
          codigo: initialPlanData.vendedorS4eId,
          tipoCobranca: 1,
          adesionista: 0,
          maxMensalidadeId: '1',
        },
        responsavelFinanceiro: {
          codigoContrato:
            data.Associado.selecionado.codigoDaEmpresa == 26710
              ? 27184
              : initialPlanData.produtoComercial.formasPagamentoIndividual
                  .cd_cod_contrato_s4_e,
          nome: data.Associado.name,
          nomeMae: data.Associado.selecionado.dependentes.nomeMaeDependente,
          dataNascimento: formatDate(data.Associado.birthDate),
          cpf: data.Associado.cpf?.replace(/[.-]/g, ''),
          sexo: data.Associado.selecionado?.sexo == '1' ? 0 : 1,
          identidadeNumero: data.Associado.selecionado?.rg,
          identidadeOrgaoExpeditor: orgaoExp?.sigla || '',
          enderecoBoleto: '',
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          agencia: 0,
          agencia_dv: 0,
          conta: 0,
          conta_dv: 0,
          contaOperacao: 0,
          matricula: data.Associado.matricula,
          dataApresentacao: '',
          diaVencimento: '01',
          tipoPagamento:
            data.Associado.selecionado.codigoDaEmpresa === 26710 ? 205 : 529,
          origemVenda: 13,
          observacaoAssociado: '',
          codSistemaExterno: '',
          dataAssinaturaContrato: vigencia.toISOString().split('T')[0],
          numeroProposta: propostaNr,
          senhaAssociado: '',
          endereco: {
            cep: data.Associado.selecionado?.cep,
            logradouro: endereco.data.dados.Logradouro,
            numero: String(data.Associado.selecionado?.number),
            complemento: data.Associado.selecionado?.complemento || '',
            bairro: String(endereco.data.dados.IdBairro),
            municipio: String(endereco.data.dados.IdMunicipio),
            uf: String(endereco.data.dados.IdUf),
            descricaoUf: endereco.data.dados.Uf,
          },
          fl_AlteraSituacao: 16,
          contatoResponsavelFinanceiro: contatos,
        },
        dependente: dependentesComTitular,
      },
      empresa: '',
      send: {
        nome: data.Associado.name,
        cpf: data.Associado.cpf,
        matricula: data.Associado.matricula,
        valor: changePlan
          ? String(
              calculaValor +
                parseFloat(data.Associado?.plano_vl?.replace(',', '.') || '0'),
            )
          : String(calculaValor + parseFloat(calculaValorAtual)),
      },
    };
    return checkoutData;
  };

  const onSubmit: SubmitHandler<PaymentFormSchema> = async data => {
    const adesao = calculateDueDate();
    const MonthYear = getMonthAndYear();
    const formatDate = (date: string) => {
      const [day, month, year] = date.split('/');
      return `${year}-${month}-${day}`;
    };

    const newData = {
      token: initialPlanData.token,
      dados: {
        parceiro: {
          codigo: initialPlanData.vendedorS4eId,
          adesionista: 0,
        },
        responsavelFinanceiro: {
          codigo: data.Associado.id,
          dataAssinaturaContrato: formatDate(adesao),
        },
        dependente: (data.dependents ?? []).map(dependent => ({
          tipo:
            dependent.parentage == '10' || dependent.parentage == '3'
              ? 3
              : dependent.parentage == '11' || dependent.parentage == '4'
              ? 4
              : 10,
          nome: dependent.name,
          cpf: dependent.cpf?.replace(/[.-]/g, ''),
          sexo: dependent.gender == '1' ? 0 : 1,
          plano: dependent.plano
            ? parseFloat(dependent.plano)
            : data.Associado.plano,
          planoValor: dependent.plano_vl
            ? dependent.plano_vl.replace(',', '.')
            : data?.Associado?.plano_vl?.replace(',', '.'),
          numeroProposta: '',
          carenciaAtendimento: 0,
          rcaId: 0,
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: dependent.mothersName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(dependent.birthDate),
          funcionarioCadastro: 0,
          dataCadastroLoteContrato: '',
        })),
        contatoDependente: [],
      },
    };
    setLoading(true);
    if (
      initialPlanData.produtoComercial.formasPagamentoIndividual
        .cd_cod_contrato_s4_e == 27855
    ) {
      const Url = process.env.REACT_APP_OG_POST_INCLUSAO_DEPENDENTE;
      try {
        const response = await axios.post(Url, newData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const responseData = response.data;

        setLoading(false);
        setError(false);
        setResponse(responseData);
        goToNextStep();
        navigate('/proposta', { state: responseData });
      } catch (error: any) {
        setLoading(false);
        setError(true);

        const errorData = error.response?.data;

        if (errorData?.code) {
          setTitle(errorData.code);
          setText(errorData.error);
        } else {
          setText(errorData?.message || 'Erro inesperado');
        }
      }
    } else {
      const Url = `${process.env.REACT_APP_OG_API_URL}/include/dependentes`;
      const flowId = uuid();
      const checkoutData = await gerarDadosCheckout(
        data,
        Number(MonthYear),
        flowId,
      );
      console.log('checkoutData: ', checkoutData);
      try {
        const response = await axios.post(Url, checkoutData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.data.LinkAssinatura) {
          setLinkData(response.data.LinkAssinatura);
          handleSubmit(goToNextForm)();

          await aguardarAssinatura({
            flowId,
            onConfirm: async () => {
              setLinkData(null);
              const responseData = response.data;

              setLoading(false);
              setError(false);
              setResponse(responseData);
              navigate('/proposta', { state: responseData });
            },
          });
          return;
        } else {
          alert('Erro ao gerar link de assinatura. Tente novamente.');
          setLoading(false);
          return;
        }
      } catch (error: any) {
        setLoading(false);
        setError(true);

        const errorData = error.response?.data;

        if (errorData?.code) {
          setTitle(errorData.code);
          setText(errorData.error);
        } else {
          setText(errorData?.message || 'Erro inesperado');
        }
      }
    }
  };

  const navigateToProposta = () => {
    if (storedResponse) {
      navigate('/proposta', { state: storedResponse });
    }
  };

  return (
    <HirePlanFormContext.Provider
      value={{
        register,
        control,
        handleSubmit,
        watch,
        onSubmit,
        goToNextForm,
        errors,
        clearErrors,
        unregister,
        setValue,
        getValues,
        trigger,
        navigateToProposta,
      }}
    >
      {children}
    </HirePlanFormContext.Provider>
  );
}

export function useUpdateDependentForm(): HirePlanFormContextProps {
  return useContext(HirePlanFormContext);
}
